import {
  SquaresFour, CalendarBlank, Wrench, Users, Cube, Stack, ChartBar, Gear,
  MagnifyingGlass, Bell, DotsThree, MapPin, Clock, CheckCircle,
} from "@phosphor-icons/react";

/* Shared chrome ---------------------------------------------------------- */
function Sidebar({ active = "Dashboard" }) {
  const items = [
    { label: "Dashboard", icon: SquaresFour },
    { label: "Calendar", icon: CalendarBlank },
    { label: "Jobs", icon: Wrench },
    { label: "Customers", icon: Users },
    { label: "Assets", icon: Cube },
    { label: "Stock", icon: Stack },
    { label: "Reports", icon: ChartBar },
  ];
  return (
    <aside className="hidden w-[130px] flex-none flex-col gap-1 border-r border-white/10 bg-[#0c0d10] p-3 sm:flex">
      <div className="mb-3 flex items-center gap-2 px-1">
        <span className="flex h-6 w-6 items-center justify-center rounded-md bg-primary text-[10px] font-black text-primary-foreground">FC</span>
        <span className="text-[11px] font-bold text-white">FlowCore</span>
      </div>
      {items.map((it) => {
        const Icon = it.icon;
        const on = it.label === active;
        return (
          <div key={it.label} className={`flex items-center gap-2 rounded-md px-2 py-1.5 text-[10px] font-medium ${on ? "bg-primary/15 text-primary" : "text-white/45"}`}>
            <Icon size={13} weight={on ? "fill" : "regular"} /> {it.label}
          </div>
        );
      })}
      <div className="mt-auto flex items-center gap-2 rounded-md px-2 py-1.5 text-[10px] font-medium text-white/45">
        <Gear size={13} /> Settings
      </div>
    </aside>
  );
}

function TopBar({ title }) {
  return (
    <div className="flex items-center justify-between border-b border-white/10 px-4 py-2.5">
      <div>
        <p className="text-[9px] uppercase tracking-wider text-white/35">FlowCore</p>
        <h4 className="text-[13px] font-bold text-white">{title}</h4>
      </div>
      <div className="flex items-center gap-2">
        <div className="hidden items-center gap-1.5 rounded-md border border-white/10 px-2 py-1 text-[9px] text-white/40 sm:flex">
          <MagnifyingGlass size={11} /> Search jobs…
        </div>
        <span className="relative flex h-6 w-6 items-center justify-center rounded-md border border-white/10 text-white/50">
          <Bell size={12} />
          <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-primary" />
        </span>
        <span className="h-6 w-6 rounded-full bg-gradient-to-br from-primary to-emerald-700" />
      </div>
    </div>
  );
}

/* Dashboard -------------------------------------------------------------- */
export function OpsDashboard() {
  const kpis = [
    { label: "Jobs today", value: "248", delta: "+12%" },
    { label: "Engineers live", value: "36", delta: "94% util" },
    { label: "Revenue MTD", value: "£184k", delta: "+8%" },
    { label: "Overdue PPM", value: "3", delta: "−40%" },
  ];
  const bars = [42, 58, 35, 72, 64, 88, 54, 76, 60, 92, 70, 48];
  const jobs = [
    { c: "Halton Gates Ltd", t: "Gate motor service", s: "In progress", col: "text-primary" },
    { c: "Northwood FM", t: "Fire alarm inspection", s: "Scheduled", col: "text-blue-400" },
    { c: "City Plumbing Co", t: "Boiler fault call-out", s: "Complete", col: "text-white/40" },
  ];
  return (
    <div className="flex h-full w-full bg-[#0a0b0d] text-white">
      <Sidebar active="Dashboard" />
      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar title="Operations Dashboard" />
        <div className="grid flex-1 grid-cols-4 gap-2.5 p-4">
          {kpis.map((k) => (
            <div key={k.label} className="col-span-2 rounded-lg border border-white/10 bg-[#121317] p-3 sm:col-span-1">
              <p className="text-[9px] text-white/40">{k.label}</p>
              <p className="mt-1 text-lg font-extrabold leading-none">{k.value}</p>
              <p className="mt-1 text-[9px] font-semibold text-primary">{k.delta}</p>
            </div>
          ))}
          <div className="col-span-4 rounded-lg border border-white/10 bg-[#121317] p-3 sm:col-span-2">
            <div className="flex items-center justify-between">
              <p className="text-[10px] font-semibold">Jobs completed / week</p>
              <DotsThree size={16} className="text-white/30" />
            </div>
            <div className="mt-3 flex h-24 items-end gap-1.5">
              {bars.map((b, i) => (
                <div key={i} className="flex-1 rounded-t-sm" style={{ height: `${b}%`, background: i % 3 === 2 ? "#10b981" : "rgba(16,185,129,0.35)" }} />
              ))}
            </div>
          </div>
          <div className="col-span-4 rounded-lg border border-white/10 bg-[#121317] p-3 sm:col-span-2">
            <p className="text-[10px] font-semibold">Live jobs</p>
            <div className="mt-2 space-y-1.5">
              {jobs.map((j) => (
                <div key={j.c} className="flex items-center justify-between rounded-md bg-white/[0.03] px-2.5 py-2">
                  <div className="min-w-0">
                    <p className="truncate text-[10px] font-semibold">{j.c}</p>
                    <p className="truncate text-[9px] text-white/40">{j.t}</p>
                  </div>
                  <span className={`ml-2 flex-none text-[9px] font-bold ${j.col}`}>{j.s}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* Calendar --------------------------------------------------------------- */
export function OpsCalendar() {
  const engineers = ["A. Mercer", "J. Okafor", "S. Patel", "L. Brennan", "D. Whyte"];
  const blocks = [
    [{ s: 0, w: 2, c: "#10b981", t: "Gate service" }, { s: 3, w: 2, c: "#3b82f6", t: "PPM visit" }],
    [{ s: 1, w: 3, c: "#3b82f6", t: "Fire alarm" }, { s: 5, w: 1, c: "#10b981", t: "Call-out" }],
    [{ s: 0, w: 1, c: "#f59e0b", t: "Survey" }, { s: 2, w: 3, c: "#10b981", t: "Install" }],
    [{ s: 2, w: 2, c: "#10b981", t: "Barrier fix" }, { s: 4, w: 2, c: "#a855f7", t: "Access ctrl" }],
    [{ s: 1, w: 2, c: "#3b82f6", t: "HVAC" }, { s: 4, w: 1, c: "#f59e0b", t: "Quote" }],
  ];
  const hours = ["08", "09", "10", "11", "12", "13"];
  return (
    <div className="flex h-full w-full bg-[#0a0b0d] text-white">
      <Sidebar active="Calendar" />
      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar title="Operations Calendar" />
        <div className="flex-1 overflow-hidden p-4">
          <div className="grid grid-cols-[70px_repeat(6,1fr)] gap-1 text-[9px] text-white/40">
            <span />
            {hours.map((h) => <span key={h} className="text-center">{h}:00</span>)}
          </div>
          <div className="mt-1 space-y-1">
            {engineers.map((e, ri) => (
              <div key={e} className="grid grid-cols-[70px_repeat(6,1fr)] items-center gap-1">
                <div className="flex items-center gap-1.5 truncate">
                  <span className="h-4 w-4 flex-none rounded-full bg-gradient-to-br from-primary to-emerald-700" />
                  <span className="truncate text-[9px] font-medium text-white/70">{e}</span>
                </div>
                <div className="relative col-span-6 h-7 rounded bg-white/[0.02]">
                  <div className="absolute inset-0 grid grid-cols-6">
                    {hours.map((_, i) => <div key={i} className="border-r border-white/[0.04]" />)}
                  </div>
                  {blocks[ri].map((b, bi) => (
                    <div
                      key={bi}
                      className="absolute top-1 flex h-5 items-center rounded px-1.5 text-[8px] font-semibold text-black"
                      style={{ left: `${(b.s / 6) * 100}%`, width: `${(b.w / 6) * 100}%`, background: b.c }}
                    >
                      <span className="truncate">{b.t}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* Engineer mobile app ---------------------------------------------------- */
export function EngineerApp() {
  const steps = [
    { t: "Arrived on site", d: "09:42 · geo-verified", done: true },
    { t: "Asset inspected", d: "Gate motor · SN 4471", done: true },
    { t: "Parts fitted", d: "2 items from van stock", done: true },
    { t: "Customer signature", d: "Awaiting sign-off", done: false },
  ];
  return (
    <div className="flex h-full w-full items-center justify-center bg-[#0a0b0d] p-4">
      <div className="w-full max-w-[220px] overflow-hidden rounded-[1.4rem] border border-white/10 bg-[#101216]">
        <div className="flex items-center justify-between bg-primary px-3 py-2 text-black">
          <span className="text-[10px] font-black">FlowEngineer</span>
          <span className="text-[9px] font-semibold">09:51</span>
        </div>
        <div className="p-3">
          <p className="text-[9px] text-white/40">Job #FC-20481</p>
          <p className="text-[12px] font-bold text-white">Gate motor service</p>
          <div className="mt-1 flex items-center gap-1 text-[9px] text-white/50">
            <MapPin size={10} className="text-primary" /> Halton Gates Ltd, Warrington
          </div>
          <div className="mt-3 space-y-2">
            {steps.map((s) => (
              <div key={s.t} className="flex items-start gap-2">
                <CheckCircle size={14} weight={s.done ? "fill" : "regular"} className={s.done ? "text-primary" : "text-white/25"} />
                <div>
                  <p className="text-[10px] font-semibold text-white">{s.t}</p>
                  <p className="text-[8px] text-white/40">{s.d}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3 flex items-center gap-1.5 rounded-lg bg-white/[0.03] px-2 py-1.5 text-[9px] text-white/50">
            <Clock size={11} className="text-primary" /> On site 09m · SLA on track
          </div>
          <div className="mt-3 rounded-lg bg-primary py-1.5 text-center text-[10px] font-bold text-black">
            Complete &amp; Sign Off
          </div>
        </div>
      </div>
    </div>
  );
}

/* Reusable browser frame wrapper ---------------------------------------- */
export function BrowserFrame({ children, label, className = "" }) {
  return (
    <div className={`overflow-hidden rounded-2xl border border-white/10 bg-card ${className}`}>
      <div className="flex items-center gap-1.5 border-b border-white/10 bg-black/50 px-4 py-2.5">
        <span className="h-2.5 w-2.5 rounded-full bg-primary/60" />
        <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
        <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
        {label && <span className="ml-2 truncate text-[11px] text-muted-foreground">{label}</span>}
      </div>
      <div className="aspect-[16/10] w-full">{children}</div>
    </div>
  );
}
